import { useState } from "react";
import api, { API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { SignaturePad } from "@/components/SignaturePad";
import { ShieldAlert, Eye, PenLine } from "lucide-react";
import { toast } from "sonner";

function tok() { return localStorage.getItem("fl_token") || ""; }
function fileUrl(path) {
  return `${API}/files/view?path=${encodeURIComponent(path)}&auth=${encodeURIComponent(tok())}`;
}

// Crew-facing "you still need to sign this SSSP" prompt. Used on the run-sheet
// (today) and the Tomorrow card (schedule). Renders one amber card per unsigned
// SSSP with a read + draw-to-sign flow.
export function SsspSignPrompt({ items = [], testidPrefix = "sssp" }) {
  const [signedIds, setSignedIds] = useState([]);
  const [signDoc, setSignDoc] = useState(null);
  const pending = items.filter((i) => !signedIds.includes(i.id));
  if (pending.length === 0) return null;

  const onSign = async (dataUrl) => {
    if (!signDoc) return;
    try {
      await api.post(`/job-sssp/docs/${signDoc.id}/sign`, { signature: dataUrl });
      toast.success("Signed — thanks");
      setSignedIds((s) => [...s, signDoc.id]);
      setSignDoc(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save signature");
    }
  };

  return (
    <div className="mt-3 pt-3 border-t border-zinc-200 dark:border-white/10 space-y-2" data-testid={`${testidPrefix}-sssp-signoff`}>
      {pending.map((s) => (
        <div key={s.id}
             className="rounded-xl border border-amber-300 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/10 p-3"
             data-testid={`${testidPrefix}-sssp-unsigned-${s.id}`}>
          <div className="flex items-start gap-2">
            <ShieldAlert className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700 dark:text-amber-400">Action needed · Sign SSSP</div>
              <div className="text-sm font-bold text-zinc-900 dark:text-white truncate mt-0.5">{s.name}</div>
            </div>
          </div>
          <div className="mt-2 flex items-center gap-2">
            <a href={fileUrl(s.path)} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-zinc-700 dark:text-zinc-300 hover:text-[#FF5F15]"
               data-testid={`${testidPrefix}-sssp-view-${s.id}`}>
              <Eye className="w-3.5 h-3.5" /> Read
            </a>
            <Button size="sm" onClick={() => setSignDoc(s)}
                    className="ml-auto rounded-sm bg-[#FF5F15] hover:bg-[#E65100] uppercase text-[11px] font-bold tracking-wider h-8"
                    data-testid={`${testidPrefix}-sssp-sign-${s.id}`}>
              <PenLine className="w-3.5 h-3.5 mr-1" /> Read &amp; sign
            </Button>
          </div>
        </div>
      ))}

      <Dialog open={!!signDoc} onOpenChange={(v) => !v && setSignDoc(null)}>
        <DialogContent data-testid={`${testidPrefix}-sssp-sign-dialog`}>
          <DialogHeader>
            <DialogTitle className="uppercase font-display text-xl">Sign off — {signDoc?.name}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-zinc-600 dark:text-zinc-300 -mt-1">
            Please read the SSSP first, then sign below to confirm you understand it.
          </p>
          {signDoc && (
            <a href={fileUrl(signDoc.path)} target="_blank" rel="noreferrer"
               className="inline-flex items-center gap-1.5 text-sm font-bold text-[#FF5F15] hover:underline"
               data-testid={`${testidPrefix}-sssp-sign-view`}>
              <Eye className="w-4 h-4" /> Open the SSSP to read
            </a>
          )}
          <div className="mt-2">
            <SignaturePad testIdPrefix={`${testidPrefix}-sssp-sig`} onSave={onSign} onCancel={() => setSignDoc(null)} />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
